import React from 'react'
import ServicePage, { SplitSection, StackRibbon } from '../ServicePage'

const MobileHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="m-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="m-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#m-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* phone (front) */}
    <g>
      <rect x="170" y="80" width="170" height="320" rx="30" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <rect x="180" y="92" width="150" height="296" rx="22" fill="rgba(0,0,0,0.20)" />
      <rect x="220" y="86" width="60" height="8" rx="4" fill="rgba(255,255,255,0.20)" />
      <rect x="195" y="115" width="120" height="12" rx="3" fill="rgba(0,212,255,0.55)" />
      <rect x="195" y="135" width="120" height="60" rx="10" fill="url(#m-grad)" opacity="0.85">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
      </rect>
      <rect x="195" y="205" width="120" height="10" rx="3" fill="rgba(255,255,255,0.30)" />
      <rect x="195" y="221" width="120" height="10" rx="3" fill="rgba(255,255,255,0.20)" />
      <rect x="195" y="237" width="80"  height="10" rx="3" fill="rgba(255,255,255,0.18)" />
      <rect x="195" y="265" width="120" height="36" rx="8" fill="rgba(255,255,255,0.08)" />
      <rect x="195" y="310" width="120" height="36" rx="8" fill="rgba(255,255,255,0.08)" />
      <rect x="225" y="362" width="60"  height="6"  rx="3" fill="rgba(255,255,255,0.20)" />
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -8;0 0" dur="6s" repeatCount="indefinite" />
    </g>

    {/* phone (back / behind) */}
    <g opacity="0.6">
      <rect x="80" y="120" width="130" height="240" rx="24" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.14)" transform="rotate(-12 145 240)" />
      <rect x="100" y="160" width="90" height="60" rx="6" fill="url(#m-grad)" opacity="0.7" transform="rotate(-12 145 240)" />
    </g>

    {/* notification badge */}
    <g>
      <circle cx="370" cy="120" r="22" fill="url(#m-grad)" />
      <text x="370" y="125" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="14" fill="#fff">9</text>
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -8;0 0" dur="4.5s" repeatCount="indefinite" />
    </g>
  </svg>
)

const MobileDevelopment = () => (
  <ServicePage
    slug="mobile-development"
    seo={{
      title: 'Mobile App Development Services',
      description:
        'Mobile app development from QA Tech — native iOS, Android and cross-platform React Native / Flutter apps, with end-to-end testing on real devices.',
      keywords:
        'mobile app development, iOS development, Android development, React Native development, Flutter development, cross-platform apps, mobile testing, native app development, UK mobile app developers',
    }}
    hero={{
      eyebrow: 'Service / Mobile Development',
      title: 'Apps your users keep on their home screen.',
      lede:
        'Native iOS and Android, cross-platform React Native and Flutter — built for performance, polish, and real-device reliability.',
    }}
    visual={<MobileHero />}
  >
    <SplitSection
      index={1}
      align="left"
      kicker="Native or Cross-platform"
      title="The right choice — not the trendy one."
    >
      <p>
        Some products demand fully native; others are perfectly served by
        React Native or Flutter at half the cost. We start with your roadmap,
        team, performance needs and audience &mdash; then recommend the
        approach that&rsquo;ll still feel right in two years.
      </p>
      <ul>
        <li>iOS &mdash; Swift, SwiftUI, Combine</li>
        <li>Android &mdash; Kotlin, Jetpack Compose, Coroutines</li>
        <li>Cross-platform &mdash; React Native, Expo, Flutter</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={2}
      align="right"
      kicker="Polish"
      title="Animation, haptics and craft."
      accent={<span className="sp-accent-code">60 fps. Always.</span>}
    >
      <p>
        On mobile, polish is the product. We obsess over transitions, gesture
        feel, haptics and offline behaviour &mdash; the small details that
        decide whether your app gets a 5-star review or an uninstall.
      </p>
    </SplitSection>

    <SplitSection
      index={3}
      align="left"
      kicker="Backend & Sync"
      title="Real-time, offline-first, conflict-aware."
    >
      <p>
        Mobile apps live and die by the backend. We design APIs and sync
        layers that handle flaky networks, background work, push, deep
        linking, and proper offline-first behaviour &mdash; not "loading
        spinner forever" when the train hits a tunnel.
      </p>
    </SplitSection>

    <SplitSection
      index={4}
      align="right"
      kicker="Testing on real devices"
      title="Emulators lie. We use real phones."
      accent={
        <span className="sp-accent-stat">
          <span className="sp-accent-stat__num">50+</span>
          <span className="sp-accent-stat__label">device matrix</span>
        </span>
      }
    >
      <p>
        We run automated tests on real-device farms (Browserstack, SauceLabs)
        across the iOS and Android versions your users actually run. Plus
        full manual exploratory testing for the things automation will never
        catch.
      </p>
      <ul>
        <li>XCUITest, Espresso, Appium, Detox, Maestro</li>
        <li>Performance &amp; battery profiling</li>
        <li>Accessibility (VoiceOver / TalkBack)</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={5}
      align="left"
      kicker="Releases"
      title="Store submissions without the drama."
    >
      <p>
        We handle App Store and Play Store releases end-to-end &mdash;
        certificates, provisioning, store listings, screenshots, staged
        rollouts, in-app updates and feature flags. You ship calmly. Users
        update happily.
      </p>
    </SplitSection>

    <SplitSection
      index={6}
      align="right"
      kicker="After launch"
      title="Crash-free isn't a vibe — it's a metric."
    >
      <p>
        Crashlytics, Sentry, performance monitoring and proper analytics
        funnels &mdash; with weekly reviews so we know what&rsquo;s working
        and what to fix before the next release.
      </p>
    </SplitSection>

    <StackRibbon
      label="Mobile stack"
      items={[
        'Swift', 'SwiftUI', 'Kotlin', 'Jetpack Compose',
        'React Native', 'Expo', 'Flutter',
        'XCUITest', 'Espresso', 'Appium', 'Detox', 'Maestro',
        'Firebase', 'Crashlytics', 'Sentry',
        'Fastlane', 'App Center', 'TestFlight',
      ]}
    />
  </ServicePage>
)

export default MobileDevelopment
